import {lazy, Suspense, useMemo} from 'react'
import {Box} from '@primer/react-brand/esm'
import {useAutoPlayVideo} from '@github-ui/swp-core/components/AutoPlayVideo/useAutoPlayVideo'
import {AutoPlayVideoButton} from '@github-ui/swp-core/components/AutoPlayVideo/AutoPlayVideoButton'

import ImageBgDesktop from '../../_assets/hero-bg-desktop.webp'
import ImageBgMobile from '../../_assets/hero-bg-mobile.webp'

const CopilotHeadWebGL = lazy(() => import('../../_components/CopilotHeadWebGL/CopilotHeadWebGL'))

type Props = {
  description?: string
  posterSrcLarge?: string
  posterSrcSmall?: string
  videoSrcLarge: string
  videoSrcSmall?: string
}

const heroVideoPlayerCopy = {
  label: {
    play: 'Play',
    pause: 'Pause',
    replay: 'Replay',
  },
  ariaLabel: {
    play: 'GitHub Copilot Chat demo video is currently paused. Click to play.',
    pause: 'GitHub Copilot Chat demo video is currently playing. Click to pause.',
    replay: 'GitHub Copilot Chat demo video has ended. Click to replay.',
  },
}

export function HeroVideo(props: Props) {
  const {description = '', posterSrcLarge, posterSrcSmall, videoSrcLarge, videoSrcSmall} = props

  const {registerVideo, buttonLabel, buttonPressed, buttonAriaLabel, buttonAnalyticsAttributes, toggle, onEnded} =
    useAutoPlayVideo({
      copy: heroVideoPlayerCopy,
      enableIntersectionObserver: false,
      analytics: {context: 'demo_gif', location: 'hero'},
    })

  const videosDOM = useMemo(
    () => (
      <>
        <video
          playsInline
          muted
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className="lp-Hero-video lp-Hero-video--landscape"
          width="1248"
          height="735"
          poster={posterSrcLarge}
          ref={registerVideo}
          onEnded={onEnded}
        >
          <source src={videoSrcLarge} type="video/mp4; codecs=avc1.4d002a" />
        </video>

        {videoSrcSmall ? (
          <video
            playsInline
            muted
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            className="lp-Hero-video lp-Hero-video--portrait"
            width="539.5"
            height="682"
            ref={registerVideo}
            poster={posterSrcSmall}
            onEnded={onEnded}
          >
            <source src={videoSrcSmall} type="video/mp4; codecs=avc1.4d002a" />
          </video>
        ) : null}
      </>
    ),
    [onEnded, posterSrcLarge, posterSrcSmall, registerVideo, videoSrcLarge, videoSrcSmall],
  )

  return (
    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
    <Box className="lp-Hero-visual">
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <Box role="img" className="lp-Hero-videoContainer" aria-label={description}>
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div className="lp-Hero-background">
          <img src={ImageBgDesktop} alt="" />
          <img src={ImageBgMobile} alt="" />
        </div>

        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div className="lp-Hero-glass">{videosDOM}</div>
      </Box>

      <AutoPlayVideoButton
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className="lp-Hero-videoPlayerButton PlayButton PlayButton--gray"
        pressed={buttonPressed}
        label={buttonLabel}
        ariaLabel={buttonAriaLabel}
        onClick={toggle}
        analyticsAttributes={buttonAnalyticsAttributes}
      />

      <Suspense>
        <CopilotHeadWebGL />
      </Suspense>
    </Box>
  )
}
